"use client"

import React, { useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import {
    Box,
    Typography,
    IconButton,
    Paper,
    LinearProgress,
    useTheme,
    alpha,
    Alert,
    Stack
} from '@mui/material'
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    Description as FileIcon,
    Image as ImageIcon,
    AudioFile as AudioIcon,
    VideoFile as VideoIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material'

export default function MultiFileUpload() {
    const theme = useTheme()
    const [files, setFiles] = useState([])
    const [uploading, setUploading] = useState(false)
    const [progress, setProgress] = useState(0)
    const [uploadComplete, setUploadComplete] = useState(false)

    const onDrop = useCallback((acceptedFiles) => {
        const newFiles = acceptedFiles.map((file) =>
            Object.assign(file, {
                preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
            })
        )
        setFiles((prevFiles) => [...prevFiles, ...newFiles])
        setUploadComplete(false)
        uploadFiles(newFiles)
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "image/*": [],
            "audio/*": [],
            "video/*": [],
            "application/pdf": [],
            "application/msword": [],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
        },
    })

    const removeFile = (fileToRemove) => {
        setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove))
        if (fileToRemove.preview) {
            URL.revokeObjectURL(fileToRemove.preview)
        }
    }

    const uploadFiles = async (filesToUpload) => {
        setUploading(true)
        setProgress(0)

        for (let i = 0; i <= 100; i++) {
            setProgress(i)
            await new Promise((resolve) => setTimeout(resolve, 50))
        }

        // Simulate an API upload here

        setUploading(false)
        setUploadComplete(true)
    }

    const getFileIcon = (file) => {
        if (file.type.startsWith("image/")) return <ImageIcon sx={{ fontSize: 40 }} />
        if (file.type.startsWith("audio/")) return <AudioIcon sx={{ fontSize: 40 }} />
        if (file.type.startsWith("video/")) return <VideoIcon sx={{ fontSize: 40 }} />
        if (file.type === "application/pdf" || file.type.includes("word")) return <PdfIcon sx={{ fontSize: 40 }} />
        return <FileIcon sx={{ fontSize: 40 }} />
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                {...getRootProps()}
                sx={{
                    p: 3,
                    border: `2px dashed ${isDragActive ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.04)
                    }
                }}
            >
                <input {...getInputProps()} />
                <UploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                    {isDragActive ? "Dosyaları buraya bırakın" : "Dosyaları buraya sürükleyin veya seçmek için tıklayın"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    (Resim, ses, video, PDF ve Word dosyaları kabul edilir)
                </Typography>
            </Box>

            <Stack spacing={2} sx={{ mt: 3 }}>
                {files.map((file, index) => (
                    <Paper
                        key={index}
                        elevation={1}
                        sx={{
                            p: 2,
                            bgcolor: theme.palette.background.paper,
                            borderRadius: 2,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.02)
                            }
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {file.preview ? (
                                    <Box
                                        component="img"
                                        src={file.preview}
                                        alt={file.name}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 1,
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ color: theme.palette.primary.main }}>
                                        {getFileIcon(file)}
                                    </Box>
                                )}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton
                                onClick={() => removeFile(file)}
                                size="small"
                                sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                    },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Paper>
                ))}
            </Stack>

            {uploading && (
                <Box sx={{ mt: 3 }}>
                    <LinearProgress 
                        variant="determinate" 
                        value={progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                        }}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Yükleniyor... {progress}%
                    </Typography>
                </Box>
            )}

            {uploadComplete && (
                <Alert 
                    severity="success"
                    sx={{ 
                        mt: 3,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        borderRadius: 2
                    }}
                >
                    <Typography variant="body2">
                        Dosyalar başarıyla yüklendi!
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                        Yeni dosyalar yüklemek için ek dosyalar seçin.
                    </Typography>
                </Alert>
            )}
        </Box>
    )
}
